import React, { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';
import dayjs from 'dayjs';

import { CasinoNewProvider as CasinoProvider, SiteConfigManager } from '@atlas/core';
import {
  ALLOWED_MISSION_RIBBONS,
  ChipsItem,
  DefaultPopup,
  getMissionTimerValue,
  Icon,
  isAvailableMission,
  isCompletedMission,
  isLockedMission,
  Label,
  MissionProgressBar,
  Timer,
  useTimerCountdownState,
} from '@atlas/ui';

import { GROUPS } from 'components/Casino/constants';
import SmarticoRecommendedGames from 'components/Smartico/SmarticoRecommendedGames';

import { formatTaskReward, renderMissionActionButton } from './smartico-helpers';

const SmarticoMissionDetailsPopup = ({
  mission,
  onClaimMissionReward,
  onClose,
  onStartMission,
}) => {
  const configDateFormat = SiteConfigManager.getConfig('config.config.date.dateFormat', 'DD.MM');
  const configTimeFormat = SiteConfigManager.getConfig('config.config.date.timeFormat', 'HH:mm');
  const dateFormat = `${configDateFormat}.YYYY ${configTimeFormat}`;
  const [hasImageError, setHasImageError] = useState(false);
  const [swiper, setSwiper] = useState(null);
  const prevSlide = () => swiper && swiper.slidePrev();
  const nextSlide = () => swiper && swiper.slideNext();
  const missionTimerValue = useMemo(() => getMissionTimerValue(mission), [mission]);
  const hasMissionProgressbar = useMemo(() => {
    if (isAvailableMission(mission) || isCompletedMission(mission)) {
      if (!mission.is_opted_in && mission.is_requires_optin) {
        return false;
      }

      return true;
    }

    return false;
  }, [mission]);
  const getMissionDescription = useMemo(
    () =>
      isLockedMission(mission) && mission.unlock_mission_description
        ? mission.unlock_mission_description
        : mission.description,
    [mission],
  );
  const getStatusTranslate = useMemo(() => {
    if (isLockedMission(mission)) {
      return 'locked';
    }

    if (isCompletedMission(mission)) {
      return 'completed';
    }

    if (isAvailableMission(mission)) {
      return 'available';
    }
  }, [mission]);

  const missionRibbon = useMemo(() => {
    if (!mission.ribbon || !ALLOWED_MISSION_RIBBONS.includes(mission.ribbon.toLowerCase())) {
      return null;
    }

    return mission.ribbon;
  }, [mission.ribbon]);

  const handleImageError = () => {
    setHasImageError(true);
  };

  const relatedGames = useMemo(() => {
    return mission.related_games
      .filter(game => game?.game_public_meta?.enabled)
      .sort((a, b) => {
        const aOrder = Number.isInteger(a?.position) ? a.position : 9999;
        const bOrder = Number.isInteger(b?.position) ? b.position : 9999;

        return aOrder - bOrder;
      })
      .map(game => ({
        iconUrl: game?.game_public_meta?.image,
        id: +game?.ext_game_id,
        name: game?.game_public_meta?.name,
        providerDisplayedName: game?.game_public_meta?.game_provider,
        providerName: game?.game_public_meta?.game_provider,
      }));
  }, [mission]);

  const onDetailsClaimMissionReward = useCallback(
    async m => {
      await onClaimMissionReward(m);
      onClose();
    },
    [onClaimMissionReward, onClose],
  );

  const onDetailsStartMission = useCallback(
    async mId => {
      await onStartMission(mId);
      onClose();
    },
    [onStartMission, onClose],
  );

  const completedDate = useMemo(() => {
    if (mission.is_completed && mission.requires_prize_claim && !mission.prize_claimed_date_ts) {
      return mission.prize_claim_expiration_date;
    }

    return mission.complete_date_ts;
  }, [mission]);

  const { diff } = useTimerCountdownState(completedDate);
  const isTimeActive = diff > 0;

  return (
    <DefaultPopup
      className="smartico-mission-details-popup"
      headerTitle="mission"
      headerTitlePosition="center"
      onClose={onClose}
    >
      <div className="mission-details">
        <div className="mission-details__container">
          <div className="mission-details__header">
            <div
              className={cx('smartico-mission-status smartico-mission-status--medium', {
                'smartico-mission-status--completed': isCompletedMission(mission),
                'smartico-mission-status--green': isAvailableMission(mission),
              })}
            >
              <Label message={getStatusTranslate} />
            </div>

            {isCompletedMission(mission) && mission.complete_date_ts ? (
              <>
                {!mission.prize_claimed_date_ts && isTimeActive ? (
                  <div className="smartico-mission-timer mission-details__timer smartico-mission-timer--medium">
                    <Icon height={12} name="mission-timer" width={12} />
                    <Timer showSeconds={false} time={completedDate} />
                  </div>
                ) : (
                  <div className="smartico-mission-timer smartico-mission-timer--medium mission-details__timer">
                    <Icon height={12} name="mission-timer" width={12} />
                    <span className="smartico-mission-timer__static">
                      {dayjs(completedDate).format(dateFormat)}
                    </span>
                  </div>
                )}
              </>
            ) : (
              missionTimerValue && (
                <div className="smartico-mission-timer mission-details__timer smartico-mission-timer--medium">
                  <Icon height={12} name="mission-timer" width={12} />
                  <Timer showSeconds={false} time={missionTimerValue} />
                </div>
              )
            )}

            {missionRibbon && (
              <div className="smartico-mission-ribbon smartico-mission-ribbon--medium mission-details__ribbon">
                <Icon
                  className="smartico-mission-ribbon__icon"
                  height={22}
                  name={`mission-${missionRibbon}`}
                  width={22}
                />

                <div className="smartico-mission-ribbon__label">{missionRibbon}</div>
              </div>
            )}
          </div>

          <div className="mission-details__main">
            <div
              className={cx('mission-details__image', {
                'mission-details__image--locked': isLockedMission(mission),
              })}
            >
              {isLockedMission(mission) && (
                <Icon className="mission-details__locked-icon" name="mission-lock" />
              )}

              {hasImageError || !mission.image ? (
                <Icon
                  className="mission-details__image-placeholder"
                  name="mission-image-placeholder"
                />
              ) : (
                <img alt={mission.name} onError={handleImageError} src={mission.image} />
              )}
            </div>
            <div className="mission-details__content">
              <div className="mission-details__name title-big-bold-text">{mission.name}</div>
              {hasMissionProgressbar && (
                <div className="mission-details__progress">
                  <MissionProgressBar progressPercent={mission.progress} />
                </div>
              )}
            </div>
          </div>
          {getMissionDescription && (
            <div className="mission-details__description sub-title-normal-text">
              {getMissionDescription}
            </div>
          )}

          {mission.reward && (
            <div className="mission-details__reward">
              <div className="mission-details__reward-header">
                <Icon height={22} name="mission-reward" width={22} />

                <Label
                  className="mission-details__reward-title sub-title-500-text"
                  message="get_reward"
                ></Label>
              </div>

              <div className="mission-details__reward-description sub-title-bold-text">
                {mission.reward}
              </div>
            </div>
          )}

          {mission.tasks.length && (
            <div className="mission-details__tasks">
              <Label
                className="mission-details__tasks-header title-text"
                message="missions_tasks"
              />
              <ul className="mission-details__tasks-list">
                {mission.tasks.map(task => (
                  <li
                    className={cx('mission-details__task', {
                      'mission-details__task--completed': task.is_completed,
                    })}
                    key={task.id}
                  >
                    <svg
                      className="mission-details__task-icon"
                      fill="none"
                      height="22"
                      viewBox="0 0 22 22"
                      width="22"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_44950_76832)">
                        <circle cx="11" cy="11.0013" fill="#BBD4F6" opacity="0.1" r="10.0833" />
                        <path
                          clipRule="evenodd"
                          d="M11 18.3346C11.963 18.3346 12.9166 18.145 13.8063 17.7764C14.696 17.4079 15.5044 16.8677 16.1854 16.1868C16.8664 15.5058 17.4065 14.6974 17.7751 13.8076C18.1436 12.9179 18.3333 11.9643 18.3333 11.0013C18.3333 10.0383 18.1436 9.08468 17.7751 8.19496C17.4065 7.30524 16.8664 6.49682 16.1854 5.81585C15.5044 5.13489 14.696 4.59472 13.8063 4.22619C12.9166 3.85765 11.963 3.66797 11 3.66797C9.05504 3.66797 7.18978 4.44059 5.81451 5.81585C4.43924 7.19112 3.66663 9.05638 3.66663 11.0013C3.66663 12.9462 4.43924 14.8115 5.81451 16.1868C7.18978 17.562 9.05504 18.3346 11 18.3346ZM10.8109 13.9672L14.885 9.07834L13.6334 8.03538L10.1297 12.239L8.31677 10.4252L7.16463 11.5774L9.60907 14.0218L10.2397 14.6525L10.8109 13.9672Z"
                          fill="#BBD4F6"
                          fillRule="evenodd"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_44950_76832">
                          <rect fill="white" height="22" width="22" />
                        </clipPath>
                      </defs>
                    </svg>

                    <div className="mission-details__task-description sub-title-normal-text">
                      {task.name}
                    </div>

                    {task.display_progress_as_count && (
                      <div className="mission-details__task-counter">
                        {task.execution_count_actual}/{task.execution_count_expected}
                      </div>
                    )}

                    {!!task.points_reward && (
                      <div className="mission-details__task-reward">
                        <Icon
                          className="mission-details__task-reward-icon"
                          height={16}
                          name="mission-coin"
                          width={16}
                        />

                        <span className="mission-details__task-reward-amount title-bold-text">
                          {formatTaskReward(task.points_reward)}
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {renderMissionActionButton(mission, {
            buttonClassName: 'mission-details__button',
            hasDetailsFallback: false,
            onClaimMissionReward: onDetailsClaimMissionReward,
            onStartMission: onDetailsStartMission,
            size: 'big',
          })}

          {!!relatedGames.length && (
            <div className="mission-details__games">
              <div className="mission-details__games-header">
                <Label
                  className="mission-details__games-title title-big-bold-text"
                  message="reccomended_for_this_mission"
                />

                {relatedGames.length > 2 && (
                  <div className="mission-details__games-arrows">
                    <ChipsItem
                      className="mission-details__games-arrow"
                      label={<Icon name="arrow-left" />}
                      onClick={prevSlide}
                    />
                    <ChipsItem
                      className="mission-details__games-arrow"
                      label={<Icon name="arrow-right" />}
                      onClick={nextSlide}
                    />
                  </div>
                )}
              </div>

              <div className="mission-details__games-slider">
                <CasinoProvider group={GROUPS.CASINO}>
                  <SmarticoRecommendedGames relatedGames={relatedGames} setSwiper={setSwiper} />
                </CasinoProvider>
              </div>
            </div>
          )}
        </div>
      </div>
    </DefaultPopup>
  );
};

export default SmarticoMissionDetailsPopup;
